import { withTopbarProps } from '@jusbrasil/topbar'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import { useCallback } from 'react'
import { URLSearchParams } from 'url'

import { AuthLayout, GoogleSignIn } from '../../components'
import { JUSID_PLATFORM_WEB_AUTH, MODAL_EVENTS, PRODUCTION_ORIGIN, ROUTES } from '../../constants'
import { type AuthPageEventData, getEventDataFromQuery } from '../../utils/event-data'
import { logNextUrlCollapse } from '../../utils/next-url-collapse-logger'
import { isValidRedirectUrl } from '../../utils/redirect-url'

function buildQueryString(query: ParsedUrlQuery, excludeKeys: string[] = []): string {
  const searchParams = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (!excludeKeys.includes(key)) {
      const paramValue = Array.isArray(value) ? value[0] : value
      if (paramValue) {
        searchParams.set(key, paramValue)
      }
    }
  })
  return searchParams.toString()
}

export interface LoginGooglePageProps {
  eventData: AuthPageEventData & { email: string }
  nextUrl?: string | null
}

export function LoginGooglePage({ eventData, nextUrl }: LoginGooglePageProps) {
  const router = useRouter()

  const handleCloseClick = useCallback(() => {
    const params: Record<string, string> = {
      app_id: eventData.appId ?? JUSID_PLATFORM_WEB_AUTH,
      email: eventData.email,
      jusid_platform: JUSID_PLATFORM_WEB_AUTH,
      type: 'LogInModal',
    }
    if (eventData.featureCode) params.feature_code = eventData.featureCode
    if (eventData.fromComponent) params.from_component = eventData.fromComponent
    sendMetrics(MODAL_EVENTS.CLOSED, null, params)
    window.location.href = PRODUCTION_ORIGIN
  }, [eventData])

  const handleUseAnotherAccount = useCallback(() => {
    const { email: _email, ...query } = router.query
    router.push({ pathname: ROUTES.SIGN_IN.BEGIN, query })
  }, [router])

  return (
    <AuthLayout>
      <GoogleSignIn
        eventData={eventData}
        email={eventData.email}
        nextUrl={nextUrl ?? undefined}
        onUseAnotherAccount={handleUseAnotherAccount}
        onCloseClick={handleCloseClick}
      />
    </AuthLayout>
  )
}

const getServerSideProps: GetServerSideProps = async (ctx) => {
  const eventData = getEventDataFromQuery(ctx.query)

  if (!eventData.email) {
    const queryString = buildQueryString(ctx.query, ['email'])
    return {
      redirect: {
        destination: `${ROUTES.SIGN_IN.BEGIN}${queryString ? `?${queryString}` : ''}`,
        permanent: false,
      },
    }
  }

  const rawNextUrl = getFirstQueryParam(ctx.query.next_url) ?? null
  const nextUrl = rawNextUrl && isValidRedirectUrl(rawNextUrl) ? rawNextUrl : null
  logNextUrlCollapse({ step: 'login-google', raw: rawNextUrl, jdid: ctx.req.cookies?.jdid ?? null })

  return {
    props: {
      eventData,
      nextUrl,
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
    },
  }
}

export const LoginGooglePageExports = {
  getServerSideProps,
}
